/**
 * Single source of truth for the SI pipeline stage layout.
 *
 * To add, remove, or reconfigure a timeline stage (tasks, dialogs, actions)
 * edit ONLY this file — the timeline component and workflow facade both derive
 * their behaviour from it at runtime.
 */

export type StageDialog = 'file-selection' | 'template-selection';

export interface PipelineStageConfig {
  /** Matches the TimelineStep.id used in the timeline component */
  stageId: 'geocut' | 'analyzed' | 'planned';
  /** Human-readable German label shown on the timeline node */
  label: string;
  /** SI task numbers belonging to this stage, in execution order */
  tasks: readonly number[];
  /** The pipeline action string emitted when this stage executes */
  action: 'start-analysis' | 'start-planning';
  /**
   * Selection this stage needs before it can run.
   * null → nothing to select; execute directly.
   * Rule: asked whenever the dispatched task window covers any task of this
   *       stage — including mid-stage continuations and single-task reruns,
   *       because the selection feeds the task itself, not the stage entry.
   */
  startDialog: StageDialog | null;
}

export const PIPELINE_STAGE_CONFIGS: readonly PipelineStageConfig[] = [
  {
    stageId: 'geocut',
    label: 'Geodaten zuschneiden',
    tasks: [1],
    action: 'start-analysis',
    startDialog: null,
  },
  {
    stageId: 'analyzed',
    label: 'Analyse',
    tasks: [2, 3],
    action: 'start-analysis',
    startDialog: 'file-selection',
  },
  {
    stageId: 'planned',
    label: 'Planung',
    tasks: [4, 5],
    action: 'start-planning',
    startDialog: 'template-selection',
  },
] as const;

/** First task of the pipeline (geocut). */
export const FIRST_PIPELINE_TASK = PIPELINE_STAGE_CONFIGS[0].tasks[0];

/** Last task in the analysis pipeline. PDF reports are separate repeatable jobs. */
export const LAST_PIPELINE_TASK =
  PIPELINE_STAGE_CONFIGS[PIPELINE_STAGE_CONFIGS.length - 1].tasks.at(-1)!;

// ─── Lookup helpers ───────────────────────────────────────────────────────────

/** Find config by stageId. Returns undefined for unknown ids. */
export function getStageConfig(
  stageId: string,
): PipelineStageConfig | undefined {
  return PIPELINE_STAGE_CONFIGS.find((c) => c.stageId === stageId);
}

/** Find config by any task number within its range. */
export function getStageConfigForTask(
  taskNo: number,
): PipelineStageConfig | undefined {
  return PIPELINE_STAGE_CONFIGS.find((c) =>
    (c.tasks as readonly number[]).includes(taskNo),
  );
}

// ─── Pure utilities ───────────────────────────────────────────────────────────

/**
 * Compute the task window to execute for a stage action.
 *
 * - trigger === 'rerun' → full stage range (tasks[0] → tasks[last])
 * - trigger === 'start' → resume from (currentTask + 1) if mid-stage,
 *                         otherwise start from tasks[0]
 */
export function resolveTaskWindowForStage(
  config: PipelineStageConfig,
  trigger: 'rerun' | 'start',
  currentTask: number,
): { startTask: number; endTask: number } {
  const first = config.tasks[0];
  const last = config.tasks[config.tasks.length - 1];

  if (trigger === 'rerun') {
    return { startTask: first, endTask: last };
  }

  // Mid-stage continuation: currentTask completed inside stage but not at end
  if (currentTask >= first && currentTask < last) {
    return { startTask: currentTask + 1, endTask: last };
  }

  return { startTask: first, endTask: last };
}

/**
 * Every stage that needs a selection (file, template) and whose tasks intersect
 * the dispatched window, in execution order.
 *
 * The single gate all execution paths share — see ExecutionPreflightService.
 * Gating on intersection rather than "startTask === tasks[0]" is deliberate: a
 * rerun of task 3 alone still consumes the selected file, and a rerun of task 5
 * alone still consumes the selected template.
 */
export function stagesNeedingSelection(taskWindow: {
  startTask: number;
  endTask: number;
}): PipelineStageConfig[] {
  return PIPELINE_STAGE_CONFIGS.filter(
    (c) =>
      c.startDialog !== null &&
      taskWindow.endTask >= c.tasks[0] &&
      taskWindow.startTask <= c.tasks[c.tasks.length - 1],
  );
}
